/* eslint-disable @typescript-eslint/no-explicit-any */
import { Object as FabricObject, Canvas } from 'fabric';

import { useCanvasStore } from '../store';

import { Layer, LayerObject } from '../types/types';

const layerPriority = ['background', 'middle', 'foreground', 'overlay'];

// Утилитарные функции, независимые от стора
export const addObjectToLayerUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  layer: Layer,
  object: FabricObject,
  zIndex: number,
  addLayerObjectCallback: (layer: Layer, layerObject: LayerObject) => void,
  canvasId?: string,
) => {
  const layerObject: LayerObject = {
    object,
    layer,
    zIndex,
  };

  addLayerObjectCallback(layer, layerObject);
  changeZIndexUtil(fabricInstance, layersMap, layer, object, zIndex, canvasId);
};

export const changeZIndexUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  layer: Layer,
  object: FabricObject,
  newZIndex: number,
  canvasId?: string,
  removeLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  addLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
) => {
  // Найти объект в слое
  const layerObject = layersMap[layer].find((lo) => lo.object === object);

  if (layerObject) {
    // Удалить объект из текущего слоя (если есть callback)
    if (removeLayerObjectCallback) {
      removeLayerObjectCallback(layer, layerObject);
    }

    // Обновить zIndex в layerObject и на самом Fabric-объекте
    layerObject.zIndex = newZIndex;
    (object as any).zIndex = newZIndex;

    // Добавить объект обратно в слой с обновленным zIndex (если есть callback)
    if (addLayerObjectCallback) {
      addLayerObjectCallback(layer, layerObject);
    }

    // Обновить порядок объектов на canvas
    reorderCanvasObjects(fabricInstance, layersMap, canvasId);
  }
};

export const moveObjectToLayerUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  object: FabricObject,
  fromLayer: Layer,
  toLayer: Layer,
  canvasId?: string,
  zIndex?: number,
  removeLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  addLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
) => {
  // Найти объект в исходном слое
  const layerObject = layersMap[fromLayer].find((lo) => lo.object === object);

  if (layerObject) {
    const oldZIndex = layerObject.zIndex;
    const newZIndex = zIndex !== undefined ? zIndex : oldZIndex;

    console.log('🔄 [moveObjectToLayerUtil] Начало перемещения объекта:', {
      objectId: (object as any).id,
      objectType: object.type,
      fromLayer,
      toLayer,
      oldZIndex,
      newZIndex,
      fromLayerObjectsBefore: layersMap[fromLayer].length,
      toLayerObjectsBefore: layersMap[toLayer].length,
    });

    // Удалить объект из исходного слоя
    if (removeLayerObjectCallback) {
      removeLayerObjectCallback(fromLayer, layerObject);
    }

    // Обновить слой и zIndex
    layerObject.layer = toLayer;
    if (zIndex !== undefined) {
      layerObject.zIndex = zIndex;
    }

    // ВАЖНО: Обновляем свойство layer объекта на canvas, чтобы syncLayersMapWithCanvas мог найти его в правильном слое
    (object as any).layer = toLayer;
    if (zIndex !== undefined) {
      (object as any).zIndex = zIndex;
    }

    // Добавить объект в новый слой
    if (addLayerObjectCallback) {
      addLayerObjectCallback(toLayer, layerObject);
    }

    console.log('✅ [moveObjectToLayerUtil] Объект перемещен, начинаем reorderCanvasObjects:', {
      objectId: (object as any).id,
      fromLayer,
      toLayer,
      newZIndex,
    });

    // Обновить порядок объектов на canvas с визуальным перемещением
    reorderCanvasObjects(fabricInstance, layersMap, canvasId, true);
  } else {
    console.error('❌ [moveObjectToLayerUtil] Объект не найден в исходном слое:', {
      objectId: (object as any).id,
      objectType: object.type,
      fromLayer,
      fromLayerObjects: layersMap[fromLayer].map((lo) => ({
        id: (lo.object as any).id,
        type: lo.object.type,
        zIndex: lo.zIndex,
      })),
    });
  }
};

export const moveObjectUpLayerUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  object: FabricObject,
  removeLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  addLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  canvasId?: string,
) => {
  // Получаем актуальный layersMap из store, если передан canvasId
  let actualLayersMap = layersMap;
  if (canvasId) {
    const { getCanvasState } = useCanvasStore.getState();
    const canvasData = getCanvasState(canvasId);
    if (canvasData?.layersMap) {
      actualLayersMap = canvasData.layersMap;
      console.log('🔄 [moveObjectUpLayerUtil] Используем актуальный layersMap из store');
    }
  }

  // Синхронизируем layersMap с canvas перед поиском
  const syncedLayersMap = syncLayersMapWithCanvas(fabricInstance, actualLayersMap);

  // Проверяем, в каком слое объект находится после синхронизации
  const objectId = (object as any).id;
  let foundLayer: Layer | null = null;
  for (const layer of Object.keys(syncedLayersMap) as Layer[]) {
    const found = syncedLayersMap[layer].find((lo) => (lo.object as any).id === objectId);
    if (found) {
      foundLayer = layer;
      console.log('📍 [moveObjectUpLayerUtil] Объект найден после syncLayersMapWithCanvas:', {
        layer,
        zIndex: found.zIndex,
        objectId,
      });
      break;
    }
  }

  // Если объект не найден в syncedLayersMap, но есть на canvas, проверяем его свойство layer
  if (!foundLayer) {
    const objectLayer = (object as any).layer;
    if (objectLayer && ['background', 'middle', 'foreground', 'overlay'].includes(objectLayer)) {
      console.log(
        '⚠️ [moveObjectUpLayerUtil] Объект не найден в syncedLayersMap, используем свойство layer:',
        {
          objectId,
          objectLayer,
        },
      );
      // Добавляем объект в правильный слой в syncedLayersMap
      const defaultLayerObject: LayerObject = {
        object,
        layer: objectLayer as Layer,
        zIndex: (object as any).zIndex || 0,
      };
      syncedLayersMap[objectLayer as Layer].push(defaultLayerObject);
      foundLayer = objectLayer as Layer;
    }
  }

  // Найти текущий слой объекта
  // Используем foundLayer из предыдущей проверки, если он был найден
  let currentLayer: Layer | null = foundLayer;

  if (!currentLayer) {
    // Если не нашли через foundLayer, ищем по ссылке на объект
    for (const layer of Object.keys(syncedLayersMap) as Layer[]) {
      const foundInLayer = syncedLayersMap[layer].some((lo) => lo.object === object);

      if (foundInLayer) {
        currentLayer = layer;
        break;
      }
    }
  }

  if (!currentLayer) {
    const objectOnCanvas = fabricInstance.getObjects().some((o) => o === object);
    const objectLayer = (object as any).layer;
    const objectZIndex = (object as any).zIndex;

    console.warn('⚠️ [moveObjectUpLayerUtil] Объект не найден в layersMap после синхронизации:', {
      objectId: (object as any).id,
      objectType: object.type,
      objectOnCanvas,
      objectLayer,
      objectZIndex,
      totalObjectsOnCanvas: fabricInstance.getObjects().length,
      layersMapStats: {
        background: syncedLayersMap.background.length,
        middle: syncedLayersMap.middle.length,
        foreground: syncedLayersMap.foreground.length,
        overlay: syncedLayersMap.overlay.length,
      },
    });
    return;
  }

  // Найти LayerObject для получения zIndex
  const currentLayerObject = syncedLayersMap[currentLayer].find((lo) => lo.object === object);
  const currentZIndex = currentLayerObject?.zIndex ?? (object as any).zIndex ?? 0;

  // Получить все объекты в текущем слое и найти min/max zIndex
  const currentLayerObjects = syncedLayersMap[currentLayer];
  const zIndicesInLayer = currentLayerObjects.map((lo) => lo.zIndex);
  const maxZIndexInLayer = zIndicesInLayer.length > 0 ? Math.max(...zIndicesInLayer) : 0;
  const minZIndexInLayer = zIndicesInLayer.length > 0 ? Math.min(...zIndicesInLayer) : 0;

  console.log('🔼 [moveObjectUpLayerUtil] Анализ текущего слоя:', {
    objectId: (object as any).id,
    objectType: object.type,
    currentLayer,
    currentZIndex,
    maxZIndexInLayer,
    minZIndexInLayer,
    objectsInLayer: currentLayerObjects.length,
  });

  // Проверяем, можем ли увеличить zIndex в текущем слое
  if (currentZIndex < maxZIndexInLayer) {
    // Увеличиваем zIndex в текущем слое
    const newZIndex = currentZIndex + 1;
    console.log('📈 [moveObjectUpLayerUtil] Увеличиваем zIndex в текущем слое:', {
      objectId: (object as any).id,
      currentLayer,
      fromZIndex: currentZIndex,
      toZIndex: newZIndex,
    });

    changeZIndexUtil(
      fabricInstance,
      syncedLayersMap,
      currentLayer,
      object,
      newZIndex,
      canvasId,
      removeLayerObjectCallback,
      addLayerObjectCallback,
    );
  } else {
    // Достигли максимума в текущем слое, переходим в следующий слой выше
    const currentLayerIndex = layerPriority.indexOf(currentLayer);

    if (currentLayerIndex < layerPriority.length - 1) {
      const nextLayer = layerPriority[currentLayerIndex + 1] as Layer;
      const nextLayerObjects = syncedLayersMap[nextLayer];
      const nextLayerZIndices = nextLayerObjects.map((lo) => lo.zIndex);
      const minZIndexInNextLayer =
        nextLayerZIndices.length > 0 ? Math.min(...nextLayerZIndices) : 0;

      // Объект поднимается из нижнего слоя - размещаем на дне верхнего слоя (равный minZIndex)
      // При равном zIndex Fabric ставит последний добавленный объект выше, но при нажатии
      // DOWN объект сразу вернется обратно - симметричное поведение с moveObjectDownLayerUtil.
      const newZIndex = minZIndexInNextLayer;

      console.log('⬆️ [moveObjectUpLayerUtil] Переходим в следующий слой:', {
        objectId: (object as any).id,
        objectType: object.type,
        from: currentLayer,
        to: nextLayer,
        fromZIndex: currentZIndex,
        toZIndex: newZIndex,
        minZIndexInNextLayer,
      });

      moveObjectToLayerUtil(
        fabricInstance,
        syncedLayersMap,
        object,
        currentLayer,
        nextLayer,
        canvasId,
        newZIndex,
        removeLayerObjectCallback,
        addLayerObjectCallback,
      );
    } else {
      console.log('❌ [moveObjectUpLayerUtil] Объект уже в самом верхнем слое (overlay)');
    }
  }
};

export const moveObjectDownLayerUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  object: FabricObject,
  removeLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  addLayerObjectCallback?: (layer: Layer, layerObject: LayerObject) => void,
  canvasId?: string,
) => {
  // Получаем актуальный layersMap из store, если передан canvasId
  let actualLayersMap = layersMap;
  if (canvasId) {
    const { getCanvasState } = useCanvasStore.getState();
    const canvasData = getCanvasState(canvasId);
    if (canvasData?.layersMap) {
      actualLayersMap = canvasData.layersMap;
      console.log('🔄 [moveObjectDownLayerUtil] Используем актуальный layersMap из store');
    }
  }

  // Синхронизируем layersMap с canvas перед поиском
  // ВАЖНО: syncLayersMapWithCanvas использует старый layersMap для определения слоя объекта
  // Поэтому если объект был перемещен, но store еще не обновился, он может вернуться в старый слой
  // Решение: используем syncLayersMapWithCanvas, но затем проверяем актуальный слой объекта на canvas
  const syncedLayersMap = syncLayersMapWithCanvas(fabricInstance, actualLayersMap);

  // Проверяем, в каком слое объект находится после синхронизации
  const objectId = (object as any).id;
  let foundLayer: Layer | null = null;
  for (const layer of Object.keys(syncedLayersMap) as Layer[]) {
    const found = syncedLayersMap[layer].find((lo) => (lo.object as any).id === objectId);
    if (found) {
      foundLayer = layer;
      console.log('📍 [moveObjectDownLayerUtil] Объект найден после syncLayersMapWithCanvas:', {
        layer,
        zIndex: found.zIndex,
        objectId,
      });
      break;
    }
  }

  // Если объект не найден в syncedLayersMap, но есть на canvas, проверяем его свойство layer
  if (!foundLayer) {
    const objectLayer = (object as any).layer;
    if (objectLayer && ['background', 'middle', 'foreground', 'overlay'].includes(objectLayer)) {
      console.log(
        '⚠️ [moveObjectDownLayerUtil] Объект не найден в syncedLayersMap, используем свойство layer:',
        {
          objectId,
          objectLayer,
        },
      );
      // Добавляем объект в правильный слой в syncedLayersMap
      const defaultLayerObject: LayerObject = {
        object,
        layer: objectLayer as Layer,
        zIndex: (object as any).zIndex || 0,
      };
      syncedLayersMap[objectLayer as Layer].push(defaultLayerObject);
      foundLayer = objectLayer as Layer;
    }
  }

  // Найти текущий слой объекта
  // Используем foundLayer из предыдущей проверки, если он был найден
  let currentLayer: Layer | null = foundLayer;

  if (!currentLayer) {
    // Если не нашли через foundLayer, ищем по ссылке на объект
    for (const layer of Object.keys(syncedLayersMap) as Layer[]) {
      const foundInLayer = syncedLayersMap[layer].some((lo) => lo.object === object);

      if (foundInLayer) {
        currentLayer = layer;
        break;
      }
    }
  }

  if (!currentLayer) {
    const objectOnCanvas = fabricInstance.getObjects().some((o) => o === object);
    const objectLayer = (object as any).layer;
    const objectZIndex = (object as any).zIndex;

    console.warn('⚠️ [moveObjectDownLayerUtil] Объект не найден в layersMap после синхронизации:', {
      objectId: (object as any).id,
      objectType: object.type,
      objectOnCanvas,
      objectLayer,
      objectZIndex,
      totalObjectsOnCanvas: fabricInstance.getObjects().length,
      layersMapStats: {
        background: syncedLayersMap.background.length,
        middle: syncedLayersMap.middle.length,
        foreground: syncedLayersMap.foreground.length,
        overlay: syncedLayersMap.overlay.length,
      },
    });
    return;
  }

  // Найти LayerObject для получения zIndex
  const currentLayerObject = syncedLayersMap[currentLayer].find((lo) => lo.object === object);
  const currentZIndex = currentLayerObject?.zIndex ?? (object as any).zIndex ?? 0;

  // Получить все объекты в текущем слое и найти min/max zIndex
  const currentLayerObjects = syncedLayersMap[currentLayer];
  const zIndicesInLayer = currentLayerObjects.map((lo) => lo.zIndex);
  const maxZIndexInLayer = zIndicesInLayer.length > 0 ? Math.max(...zIndicesInLayer) : 0;
  const minZIndexInLayer = zIndicesInLayer.length > 0 ? Math.min(...zIndicesInLayer) : 0;

  console.log('🔽 [moveObjectDownLayerUtil] Анализ текущего слоя:', {
    objectId: (object as any).id,
    objectType: object.type,
    currentLayer,
    currentZIndex,
    maxZIndexInLayer,
    minZIndexInLayer,
    objectsInLayer: currentLayerObjects.length,
  });

  // Проверяем, можем ли уменьшить zIndex в текущем слое
  if (currentZIndex > minZIndexInLayer) {
    // Уменьшаем zIndex в текущем слое
    const newZIndex = currentZIndex - 1;
    console.log('📉 [moveObjectDownLayerUtil] Уменьшаем zIndex в текущем слое:', {
      objectId: (object as any).id,
      currentLayer,
      fromZIndex: currentZIndex,
      toZIndex: newZIndex,
    });

    changeZIndexUtil(
      fabricInstance,
      syncedLayersMap,
      currentLayer,
      object,
      newZIndex,
      canvasId,
      removeLayerObjectCallback,
      addLayerObjectCallback,
    );
  } else {
    // Достигли минимума в текущем слое, переходим в следующий слой ниже
    const currentLayerIndex = layerPriority.indexOf(currentLayer);

    if (currentLayerIndex > 0) {
      const nextLayer = layerPriority[currentLayerIndex - 1] as Layer;
      const nextLayerObjects = syncedLayersMap[nextLayer];
      const nextLayerZIndices = nextLayerObjects.map((lo) => lo.zIndex);
      const maxZIndexInNextLayer =
        nextLayerZIndices.length > 0 ? Math.max(...nextLayerZIndices) : 0;

      // Объект опускается из верхнего слоя - размещаем на вершине нижнего слоя (равный maxZIndex)
      // При равном zIndex Fabric ставит последний добавленный объект выше, поэтому
      // объект визуально окажется поверх существующих. При нажатии UP он сразу вернется обратно.
      const newZIndex = maxZIndexInNextLayer;

      console.log('⬇️ [moveObjectDownLayerUtil] Переходим в следующий слой:', {
        objectId: (object as any).id,
        objectType: object.type,
        from: currentLayer,
        to: nextLayer,
        fromZIndex: currentZIndex,
        toZIndex: newZIndex,
        maxZIndexInNextLayer,
      });

      moveObjectToLayerUtil(
        fabricInstance,
        syncedLayersMap,
        object,
        currentLayer,
        nextLayer,
        canvasId,
        newZIndex,
        removeLayerObjectCallback,
        addLayerObjectCallback,
      );
    } else {
      console.log('❌ [moveObjectDownLayerUtil] Объект уже в самом нижнем слое (background)');
    }
  }
};

// Функция для синхронизации layersMap с объектами на canvas
export const syncLayersMapWithCanvas = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
): { [key in Layer]: LayerObject[] } => {
  // Получаем все объекты с canvas
  const canvasObjects = fabricInstance.getObjects();

  // Создаем новый layersMap на основе объектов с canvas
  const newLayersMap: { [key in Layer]: LayerObject[] } = {
    background: [],
    middle: [],
    foreground: [],
    overlay: [],
  };

  // Для каждого объекта на canvas пытаемся найти его в старом layersMap
  canvasObjects.forEach((canvasObject) => {
    let found = false;

    // Ищем объект во всех слоях старого layersMap
    for (const layer of Object.keys(layersMap) as Layer[]) {
      const layerObject = layersMap[layer].find((lo) => lo.object.id === canvasObject.id);

      if (layerObject) {
        // ВАЖНО: Проверяем свойство layer объекта на canvas - оно может быть более актуальным
        const canvasObjectLayer = (canvasObject as any).layer;
        const targetLayer =
          canvasObjectLayer &&
          ['background', 'middle', 'foreground', 'overlay'].includes(canvasObjectLayer)
            ? (canvasObjectLayer as Layer)
            : layerObject.layer;

        // Обновляем ссылку на объект и слой
        const updatedLayerObject = {
          ...layerObject,
          object: canvasObject, // Используем актуальную ссылку с canvas
          layer: targetLayer, // Используем слой из canvas, если он указан
        };

        newLayersMap[targetLayer].push(updatedLayerObject);
        found = true;
        break;
      }
    }

    // Если объект не найден в layersMap, добавляем в middle по умолчанию
    if (!found) {
      const defaultLayerObject: LayerObject = {
        object: canvasObject,
        layer: 'middle',
        zIndex: (canvasObject as any).zIndex || 0,
      };

      newLayersMap.middle.push(defaultLayerObject);
    }
  });

  return newLayersMap;
};

export const reorderCanvasObjects = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  canvasId?: string,
  allowVisualMovement = false,
) => {
  // ВАЖНО: Всегда синхронизируем layersMap с актуальными объектами на canvas
  const syncedLayersMap = syncLayersMapWithCanvas(fabricInstance, layersMap);

  // Обновляем store с синхронизированными данными
  if (canvasId) {
    const { getCanvasState } = useCanvasStore.getState();
    const canvasData = getCanvasState(canvasId);

    if (canvasData) {
      useCanvasStore.setState((state) => ({
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvasData,
            layersMap: syncedLayersMap,
          },
        },
      }));
    }
  }

  // Сохраняем фон канваса
  const backgroundColor = fabricInstance.backgroundColor;
  const backgroundImage = fabricInstance.backgroundImage;

  // Сохраняем активный объект
  const activeObject = fabricInstance.getActiveObject();

  // Получить все объекты из всех слоев (используем синхронизированный layersMap)
  const allLayerObjects = Object.values(syncedLayersMap).flat();

  // Отсортировать все объекты по приоритету слоя и zIndex
  allLayerObjects.sort((a, b) => {
    const layerIndexA = layerPriority.indexOf(a.layer);
    const layerIndexB = layerPriority.indexOf(b.layer);

    // Сравнить порядок слоев
    if (layerIndexA !== layerIndexB) {
      return layerIndexA - layerIndexB;
    }

    // Если слои одинаковы, сортировать по zIndex
    return a.zIndex - b.zIndex;
  });

  // ⚠️ КРИТИЧЕСКИ ВАЖНО: Защита MetaBullet системы от ложного удаления
  //
  // ПРОБЛЕМА: canvas.clear() удаляет все объекты и триггерит 'object:removed' события.
  // MetaBulletSystem.handleObjectRemoved() ловит эти события и ошибочно удаляет
  // MetaBullet-ы из внутреннего Map, считая что пользователь удалил TextEntity.
  //
  // РЕШЕНИЕ: Временно отключаем обработку 'object:removed' в MetaBulletSystem
  // перед техническими операциями, чтобы сохранить MetaBullet-ы.
  if ((fabricInstance as any)._metaBulletSystem) {
    (fabricInstance as any)._metaBulletSystem.setIgnoreObjectRemovals(true);
  }

  // Очистить canvas и добавить объекты в правильном порядке
  // ВАЖНО: Проверяем что canvas не был уничтожен
  try {
    if (fabricInstance.getContext && fabricInstance.getContext()) {
      fabricInstance.clear();
    } else {
      console.warn('⚠️ Canvas context is not available, skipping clear operation');
      return;
    }
  } catch (error) {
    console.error('❌ Error clearing canvas:', error);
    return;
  }

  // Восстанавливаем фон
  if (backgroundColor) {
    fabricInstance.backgroundColor = backgroundColor;
  }

  if (backgroundImage) {
    fabricInstance.backgroundImage = backgroundImage;
  }

  // Добавляем объекты
  allLayerObjects.forEach((layerObject) => {
    fabricInstance.add(layerObject.object);
  });

  fabricInstance.renderAll();

  // ✅ ВОССТАНАВЛИВАЕМ НОРМАЛЬНУЮ РАБОТУ: Отключаем режим игнорирования
  // После завершения reorder операции возвращаем MetaBulletSystem в нормальный режим,
  // чтобы она снова могла отслеживать реальные удаления TextEntity пользователем.
  if ((fabricInstance as any)._metaBulletSystem) {
    (fabricInstance as any)._metaBulletSystem.setIgnoreObjectRemovals(false);
  }

  // Восстанавливаем активное выделение
  if (activeObject) {
    // Даем время на обновление canvas
    requestAnimationFrame(() => {
      // Сохраняем текущее значение preserveObjectStacking
      const originalPreserveObjectStacking = fabricInstance.preserveObjectStacking;

      if (allowVisualMovement) {
        // Временно включаем preserveObjectStacking, чтобы объект остался в правильной позиции
        // после пересборки (объекты уже добавлены в правильном порядке)
        fabricInstance.preserveObjectStacking = true;
      }

      fabricInstance.setActiveObject(activeObject);
      fabricInstance.requestRenderAll();

      // Восстанавливаем исходное значение preserveObjectStacking после визуального перемещения
      if (allowVisualMovement) {
        // Используем двойной requestAnimationFrame для надежности
        requestAnimationFrame(() => {
          fabricInstance.preserveObjectStacking = originalPreserveObjectStacking;
          fabricInstance.requestRenderAll();
        });
      }
    });
  }
};

export const setLayerVisibilityUtil = (
  fabricInstance: Canvas,
  layersMap: { [key in Layer]: LayerObject[] },
  layerVisibility: { [key in Layer]: boolean },
) => {
  // Пройти по всем слоям
  Object.keys(layersMap).forEach((layer) => {
    const isVisible = layerVisibility[layer as Layer] ?? true; // Показать слой по умолчанию

    layersMap[layer as Layer].forEach((layerObject) => {
      layerObject.object.set('visible', isVisible);
    });
  });

  // Обновить отображение
  fabricInstance.renderAll();
  fabricInstance.requestRenderAll();
};

// Старые функции, использующие стор (оставляем для обратной совместимости)
export const addObjectToLayer = (
  canvasId: string,
  layer: Layer,
  object: FabricObject,
  zIndex: number,
) => {
  // Get state from store
  const { getCanvasState } = useCanvasStore.getState();

  // Get canvas data
  const canvasData = getCanvasState(canvasId);
  if (!canvasData) {
    console.warn(`Canvas with ID ${canvasId} not found.`);
    return;
  }
  // Set zIndex
  changeZIndex(canvasId, layer, object, zIndex);
};

export const setLayerVisibility = (
  canvasId: string, // Now passing canvasId
  layerVisibility: { [key in Layer]: boolean },
) => {
  const { getCanvasState } = useCanvasStore.getState();

  // Get canvas data
  const canvasData = getCanvasState(canvasId);
  if (!canvasData) {
    console.warn(`Canvas with ID ${canvasId} not found.`);
    return;
  }

  const { fabricInstance, layersMap } = canvasData;

  if (!fabricInstance || !layersMap) {
    console.warn(`Canvas with ID ${canvasId} not found.`);
    return;
  }

  setLayerVisibilityUtil(fabricInstance, layersMap, layerVisibility);
};

export const changeZIndex = (
  canvasId: string, // Passing canvasId
  layer: Layer,
  object: FabricObject,
  newZIndex: number,
) => {
  const { getCanvasState, addLayerObject, removeLayerObject } = useCanvasStore.getState();

  // Get canvas data
  const canvasData = getCanvasState(canvasId);
  if (!canvasData) {
    console.warn(`Canvas with ID ${canvasId} not found.`);
    return;
  }

  const { layersMap, fabricInstance } = canvasData;

  if (!fabricInstance) {
    console.warn(`FabricInstance for canvas ${canvasId} not found.`);
    return;
  }

  changeZIndexUtil(
    fabricInstance,
    layersMap,
    layer,
    object,
    newZIndex,
    canvasId,
    (layer, layerObject) => removeLayerObject(canvasId, layer, layerObject),
    (layer, layerObject) => addLayerObject(canvasId, layer, layerObject),
  );
};
